<template>
    <Modal
        id="run-inclusive-acceptance-modal"
        v-model:open="open"
        title="modal_title_run_inclusive_acceptance"
        @cancel="cancel"
        @submit="submit"
    >
        <div>
            <TextField
                v-if="paymentAccountType === 'lemonway'"
                v-model="wallet"
                label="wallet"
                name="wallet_id"
                help="help_run_inclusive_acceptance_wallet"
            />
        </div>
        <div>
            <Datepicker
                v-model="date"
                label="date"
                name="date"
                help="help_run_inclusive_acceptance_date"
            />
        </div>

        <div
            v-if="paymentAccountType === 'secupay'"
            class="mt-4 space-y-4"
        >
            <div class="flex justify-between text-sm">
                <span class="font-medium text-gray-700">
                    {{ $t('label_total_acceptance_amount') }}
                    <span
                        v-if="orderCount != null"
                        class="text-gray-500"
                        >({{ orderCount }} {{ $t('label_orders') }})</span
                    >
                </span>
                <span class="text-gray-900">
                    <template v-if="loadingPreview">…</template>
                    <template v-else-if="totalPaidAmount != null">
                        {{ totalPaidAmount.toFixed(2) }} {{ currency }}
                    </template>
                    <template v-else>—</template>
                </span>
            </div>
        </div>

        <!-- Modal footer -->
        <template #actions>
            <button
                :disabled="disableSubmit"
                type="submit"
                class="btn-sm bg-gray-500 text-white"
                :class="{
                    'bg-primary-accent-500 hover:bg-primary-accent-600':
                        !disableSubmit,
                }"
            >
                {{ $t('transfer') }}
            </button>
        </template>
    </Modal>
</template>
<script setup lang="ts">
import { Datepicker, Modal, TextField } from '@src/components'
import { PaymentAccountType } from '@src/enums/PaymentAccount'
import type { Asset } from '@src/types/Assets'
import { getAcceptancePreview } from '@src/utils/api/assets'
import { useVModels } from '@vueuse/core'
import { format } from 'date-fns'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
    open: boolean
    paymentAccountType: PaymentAccountType
    asset: Asset
}>()

const emit = defineEmits(['update:open', 'cancel', 'submit'])

const { open } = useVModels(props, emit)

const wallet = ref('')
const date = ref(new Date())
const totalPaidAmount = ref<number | null>(null)
const orderCount = ref<number | null>(null)
const currency = ref('EUR')
const loadingPreview = ref(false)

const disableSubmit = computed(() => {
    if (
        props.paymentAccountType === 'lemonway' &&
        (!wallet.value || !date.value)
    ) {
        return true
    }

    if (props.paymentAccountType === 'secupay' && !date.value) {
        return true
    }

    return false
})

async function fetchPreview(d: Date): Promise<void> {
    loadingPreview.value = true
    totalPaidAmount.value = null
    orderCount.value = null
    try {
        const response = await getAcceptancePreview(
            props.asset,
            format(d, 'yyyy-MM-dd')
        )
        totalPaidAmount.value = response.data.total_paid_amount
        orderCount.value = response.data.order_count
        currency.value = response.data.currency
    } catch {
        totalPaidAmount.value = null
        orderCount.value = null
    } finally {
        loadingPreview.value = false
    }
}

watch(open, (isOpen) => {
    if (isOpen && props.paymentAccountType === 'secupay')
        fetchPreview(new Date(date.value))
})

watch(date, (newDate) => {
    if (open.value && newDate && props.paymentAccountType === 'secupay') {
        fetchPreview(new Date(newDate))
    }
})

function submit(): void {
    emit('submit', {
        wallet: wallet.value,
        date: format(new Date(date.value), 'yyyy-MM-dd'),
    })
    reset()
}

function cancel() {
    emit('cancel')
    reset()
}

function reset() {
    wallet.value = ''
    date.value = new Date()
    totalPaidAmount.value = null
    orderCount.value = null
}
</script>
